import React, {createContext, useContext, useEffect, useState} from 'react';
import {getRedirectResult, onAuthStateChanged, signInWithPopup, signInWithRedirect, signOut, User} from 'firebase/auth';
import {onValue, ref} from 'firebase/database';
import {toast} from 'react-hot-toast';
import {auth, googleProvider, db} from '../utils/firebaseConfig';
import {Role, isRole, isSuperAdmin, sanitizeEmail} from '../utils/roles';
import {syncPushToken} from '../utils/push';

interface AuthContextType {
    user: User | null;
    role: Role | null;
    roomId: string | null;      // Chỉ có khi role === 'tenant'
    loading: boolean;
    isAuthenticated: boolean;   // đã đăng nhập Google VÀ có vai trò hợp lệ
    isAdmin: boolean;           // role === 'admin'
    canManage: boolean;         // admin hoặc manager (được chỉnh sửa)
    signInWithGoogle: () => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    role: null,
    roomId: null,
    loading: true,
    isAuthenticated: false,
    isAdmin: false,
    canManage: false,
    signInWithGoogle: async () => {},
    logout: () => {},
});

// Cửa sổ bật lên chỉ đáng tin trên máy tính để bàn, nên mặc định ở đây là
// chuyển hướng và popup mới là ngoại lệ - ngược với thói quen thường thấy.
//
// Lý do: trình duyệt nhúng trong Zalo/Messenger/Facebook hầu như không mở nổi
// cửa sổ thứ hai (window.open bị nuốt im lặng), còn iOS thì dọn trang nền trong
// lúc người dùng đọc màn hình chấp thuận của Google. Cả hai đều dẫn tới cùng
// một triệu chứng: bấm "Tiếp theo" xong ra trang trắng, không báo lỗi gì.
// Chuyển hướng cả trang không có cửa sổ nào để mất nên đi được ở mọi nơi.
const prefersRedirectSignIn = (): boolean => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return true;

    const ua = navigator.userAgent;

    // iPad từ iPadOS 13 khai UA giống hệt máy Mac; chỉ còn màn hình cảm ứng là
    // dấu hiệu phân biệt.
    const isIOS = /iPad|iPhone|iPod/.test(ua)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/.test(ua);

    // App đã thêm ra màn hình chính: cửa sổ bật lên rơi ra trình duyệt ngoài,
    // mất luôn đường về.
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || (window.navigator as any).standalone === true;

    return isIOS || isAndroid || isStandalone;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [user, setUser] = useState<User | null>(null);
    const [role, setRole] = useState<Role | null>(null);
    const [roomId, setRoomId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    // Theo dõi trạng thái đăng nhập Firebase
    useEffect(() => {
        return onAuthStateChanged(auth, (u) => {
            setUser(u);
            if (!u) {
                setRole(null);
                setRoomId(null);
                setLoading(false);
            }
            // Nếu có user, vai trò được resolve ở effect bên dưới
        });
    }, []);

    // Resolve vai trò theo email (realtime để đổi quyền có hiệu lực ngay)
    useEffect(() => {
        const email = user?.email?.toLowerCase();
        if (!email) {
            setRole(null);
            setRoomId(null);
            return;
        }

        if (isSuperAdmin(email)) {
            setRole('admin');
            setRoomId(null);
            setLoading(false);
            return;
        }

        // Lấy TOÀN BỘ object của userRole để đọc được cả role và roomId
        const userRoleRef = ref(db, `userRoles/${sanitizeEmail(email)}`);
        const unsub = onValue(
            userRoleRef,
            (snap) => {
                const data = snap.val();
                if (data && isRole(data.role)) {
                    setRole(data.role);
                    setRoomId(data.roomId || null);
                } else {
                    setRole(null);
                    setRoomId(null);
                }
                setLoading(false);
            },
            () => {
                setRole(null);
                setRoomId(null);
                setLoading(false);
            }
        );
        return () => unsub();
    }, [user]);

    // Token FCM tự đổi bất cứ lúc nào (trình duyệt dọn dẹp, cài lại app...).
    // Không làm mới thì token trong DB thành rác và người dùng lặng lẽ ngừng
    // nhận thông báo mà không có dấu hiệu gì. Hàm này im lặng khi chưa bật
    // quyền, nên gọi cho mọi lần đăng nhập là an toàn.
    useEffect(() => {
        if (user?.email) void syncPushToken(user.email);
    }, [user]);

    // Popup bị chặn thì SDK quay sang chuyển hướng cả trang, và kết quả chỉ về
    // sau khi tải lại app. onAuthStateChanged tự nhận user, nhưng lỗi (ví dụ
    // email không được phép) thì chỉ chỗ này thấy - không đọc là người dùng bị
    // ném về màn hình đăng nhập mà không hiểu vì sao.
    useEffect(() => {
        getRedirectResult(auth).catch((error) => {
            console.error('Google redirect sign-in error:', error);
            toast.error('Đăng nhập Google thất bại');
        });
    }, []);

    const signInWithGoogle = async () => {
        // Safari và MỌI trình duyệt trên iOS (đều chạy WebKit) xử lý cửa sổ bật
        // lên rất bấp bênh: iOS hay dọn trang nền khi người dùng chuyển qua lại,
        // và app thêm ra màn hình chính thì cửa sổ mở sang Safari - trang gốc
        // không lấy lại được kết quả, người dùng chỉ thấy "missing initial
        // state". Chuyển hướng cả trang không có cửa sổ thứ hai nào để mất, nên
        // ở đây nó là đường chính thức chứ không phải phương án chữa cháy.
        if (prefersRedirectSignIn()) {
            await signInWithRedirect(auth, googleProvider);
            return;
        }

        try {
            await signInWithPopup(auth, googleProvider);
        } catch (error: any) {
            const code = error?.code;
            if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
                await signInWithRedirect(auth, googleProvider);
                return;
            }
            if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
                console.error('Google sign-in error:', error);
                toast.error('Đăng nhập Google thất bại');
            }
        }
    };

    const logout = () => {
        signOut(auth).catch((error) => console.error('Sign-out error:', error));
    };

    const isAuthenticated = !!user && !!role;
    const isAdmin = role === 'admin';
    const canManage = role === 'admin' || role === 'manager';

    return (
        <AuthContext.Provider
            value={{user, role, roomId, loading, isAuthenticated, isAdmin, canManage, signInWithGoogle, logout}}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
